// src/components/Profile.js - VERSION CORRIGÉE ET SÉCURISÉE

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    FaUserCircle, FaTrophy, FaLock, FaCog, FaKey, 
    FaChevronLeft, FaShieldAlt, FaCheckCircle, FaTimesCircle 
} from 'react-icons/fa';

// Service API séparé
const api = axios.create({
    baseURL: 'https://api.blackroq.pro/api',
    timeout: 15000
});

// Hook personnalisé pour la gestion 2FA
const useTwoFactorAuth = (token, onSuccess) => {
    const [state, setState] = useState({
        step: 'initial', // 'initial', 'setup', 'verifying', 'success', 'error'
        qrCodeUrl: '',
        otpSecret: '',
        otpToken: '',
        message: '',
        isLoading: false
    });

    const startSetup = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true, message: '', step: 'setup' }));
        
        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await api.post('/auth/2fa/setup', {}, config);
            
            setState(prev => ({
                ...prev,
                qrCodeUrl: res.data.qrCodeUrl,
                otpSecret: res.data.secret,
                isLoading: false
            }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                message: err.response?.data?.message || 'Erreur lors de la préparation du 2FA',
                isLoading: false,
                step: 'error'
            }));
        }
    }, [token]);

    const verify = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true, message: '' }));
        
        try {
            const config = { 
                headers: { 
                    'x-auth-token': token, 
                    'Content-Type': 'application/json' 
                } 
            };
            
            const body = { 
                token: state.otpToken, 
                secret: state.otpSecret 
            };
            
            await api.post('/auth/2fa/verify', body, config);
            
            setState(prev => ({
                ...prev,
                message: '2FA activé avec succès !',
                isLoading: false,
                step: 'success'
            }));
            
            onSuccess?.();
        } catch (err) {
            setState(prev => ({
                ...prev,
                message: err.response?.data?.message || 'Code invalide. Veuillez réessayer.',
                isLoading: false,
                step: 'error'
            }));
        }
    }, [token, state.otpToken, state.otpSecret, onSuccess]);

    const disable = useCallback(async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir désactiver l\'authentification à deux facteurs ?')) {
            return false;
        }

        setState(prev => ({ ...prev, isLoading: true, message: '' }));
        
        try {
            const config = { headers: { 'x-auth-token': token } };
            await api.post('/auth/2fa/disable', {}, config);
            
            setState(prev => ({
                ...prev,
                message: '2FA désactivé avec succès',
                isLoading: false,
                step: 'initial'
            }));
            
            onSuccess?.();
            return true;
        } catch (err) {
            setState(prev => ({
                ...prev,
                message: err.response?.data?.message || 'Erreur lors de la désactivation',
                isLoading: false,
                step: 'error'
            }));
            return false;
        }
    }, [token, onSuccess]);

    const reset = useCallback(() => {
        setState({
            step: 'initial',
            qrCodeUrl: '',
            otpSecret: '',
            otpToken: '',
            message: '',
            isLoading: false
        });
    }, []);

    const updateToken = useCallback((token) => {
        setState(prev => ({ ...prev, otpToken: token }));
    }, []);

    return {
        ...state,
        startSetup,
        verify,
        disable,
        reset,
        updateToken
    };
};

// Composant Profile principal
const Profile = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    // Récupération sécurisée du token
    const getAuthToken = () => {
        try {
            return localStorage.getItem('token');
        } catch (error) {
            console.error('Erreur d\'accès au localStorage:', error);
            return null;
        }
    };
    
    const token = getAuthToken();
    
    // États simplifiés
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('info');
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    
    // Hook 2FA
    const twoFactor = useTwoFactorAuth(token, () => {
        fetchUserData(true); // Recharge les données après succès
    });

    // Fetch des données utilisateur
    const fetchUserData = useCallback(async (silent = false) => {
        if (!token) {
            navigate('/login');
            return;
        }

        if (!silent) setLoading(true);
        
        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await api.get('/profile', config);
            
            setUser(res.data.user || res.data);
            setError('');
        } catch (err) {
            console.error('Erreur chargement profil:', err);
            
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }
            
            setError(t('profileErrorLoad') || 'Erreur de chargement du profil');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [token, navigate, t]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    // Si pas de token, rediriger
    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    // Rendu conditionnel
    if (loading && !user) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('loading')}</span>
                </div>
                <p className="mt-2">{t('profileLoading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger">
                    <h5 className="alert-heading">{t('error')}</h5>
                    <p>{error}</p>
                    <button 
                        className="btn btn-outline-danger"
                        onClick={() => fetchUserData()}
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    if (!user) {
        return null; // La redirection sera gérée par useEffect
    }

    // --- Composant Changement de Mot de Passe ---
    const ChangePasswordForm = () => {
        const [formData, setFormData] = useState({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        const [status, setStatus] = useState({
            loading: false,
            message: '',
            type: '' // 'success' ou 'error'
        });

        const handleSubmit = async (e) => {
            e.preventDefault();
            setStatus({ loading: true, message: '', type: '' });

            // Validation
            if (formData.newPassword.length < 6) {
                setStatus({
                    loading: false,
                    message: 'Le mot de passe doit contenir au moins 6 caractères',
                    type: 'error'
                });
                return;
            }

            if (formData.newPassword !== formData.confirmPassword) {
                setStatus({
                    loading: false,
                    message: 'Les mots de passe ne correspondent pas',
                    type: 'error'
                });
                return;
            }

            try {
                const config = {
                    headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': token
                    }
                };

                await api.put('/user/password', {
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                }, config);

                setStatus({
                    loading: false,
                    message: 'Mot de passe mis à jour avec succès',
                    type: 'success'
                });

                // Reset form
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });

                // Masquer le formulaire après 3 secondes
                setTimeout(() => {
                    setShowPasswordForm(false);
                    setStatus({ loading: false, message: '', type: '' });
                }, 3000);

            } catch (err) {
                setStatus({
                    loading: false,
                    message: err.response?.data?.msg || 'Erreur lors de la mise à jour',
                    type: 'error'
                });
            }
        };

        return (
            <div className="mt-4 p-4 border rounded bg-light">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">
                        <FaLock className="me-2" />
                        {t('profileChangePasswordFormTitle') || 'Changer le mot de passe'}
                    </h6>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setShowPasswordForm(false)}
                        disabled={status.loading}
                    >
                        <FaTimesCircle />
                    </button>
                </div>

                {status.message && (
                    <div className={`alert alert-${status.type === 'success' ? 'success' : 'danger'}`}>
                        {status.message}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">
                            {t('currentPassword') || 'Mot de passe actuel'}
                        </label>
                        <input
                            type="password"
                            className="form-control"
                            value={formData.currentPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            required
                            disabled={status.loading}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label small fw-bold">
                            {t('newPassword') || 'Nouveau mot de passe'}
                        </label>
                        <input
                            type="password"
                            className="form-control"
                            value={formData.newPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                            minLength="6"
                            required
                            disabled={status.loading}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label small fw-bold">
                            {t('newPasswordConfirm') || 'Confirmer le mot de passe'}
                        </label>
                        <input
                            type="password"
                            className="form-control"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            minLength="6"
                            required
                            disabled={status.loading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={status.loading}
                    >
                        {status.loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                {t('loading') || 'Chargement...'}
                            </>
                        ) : (
                            t('profileButtonConfirmChange') || 'Confirmer le changement'
                        )}
                    </button>
                </form>
            </div>
        );
    };

    // --- Composant 2FA Setup ---
    const TwoFASetupComponent = () => (
        <div className="p-4 border rounded bg-white shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0 d-flex align-items-center">
                    <FaShieldAlt className="me-2 text-primary" />
                    {t('2faSetupTitle') || 'Configuration 2FA'}
                </h5>
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={twoFactor.reset}
                    disabled={twoFactor.isLoading}
                >
                    <FaChevronLeft className="me-1" />
                    {t('back') || 'Retour'}
                </button>
            </div>

            {twoFactor.message && (
                <div className={`alert alert-${twoFactor.step === 'error' ? 'danger' : 'info'}`}>
                    {twoFactor.message}
                </div>
            )}

            {twoFactor.qrCodeUrl ? (
                <>
                    <div className="alert alert-info">
                        <p className="mb-2">
                            <strong>Étape 1 :</strong> Scannez ce QR code avec votre application d'authentification
                            (Google Authenticator, Authy, etc.)
                        </p>
                        <p className="mb-0 small">
                            <strong>Clé manuelle :</strong> <code className="ms-1">{twoFactor.otpSecret}</code>
                        </p>
                    </div>

                    <div className="text-center my-4">
                        <img
                            src={twoFactor.qrCodeUrl}
                            alt="QR Code 2FA"
                            className="img-fluid border p-3 bg-white"
                            style={{ maxWidth: '250px' }}
                        />
                    </div>

                    <div className="mt-4">
                        <p className="fw-bold mb-3">
                            <strong>Étape 2 :</strong> Entrez le code à 6 chiffres généré par l'application
                        </p>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Code 2FA (6 chiffres)"
                                value={twoFactor.otpToken}
                                onChange={(e) => twoFactor.updateToken(e.target.value)}
                                maxLength="6"
                                pattern="[0-9]{6}"
                                required
                                disabled={twoFactor.isLoading}
                            />
                            <button
                                className="btn btn-success"
                                onClick={twoFactor.verify}
                                disabled={twoFactor.isLoading || twoFactor.otpToken.length !== 6}
                            >
                                {twoFactor.isLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                ) : (
                                    <FaCheckCircle className="me-2" />
                                )}
                                {t('2faButtonVerify') || 'Vérifier et Activer'}
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2">Préparation du QR Code...</p>
                </div>
            )}
        </div>
    );

    // --- Onglets ---
    const InfoTab = () => (
        <div className="card-body">
            <h5 className="card-title fw-bold mb-4">
                <FaUserCircle className="me-2 text-primary" />
                {t('profileInfoTitle') || 'Informations du Compte'}
            </h5>
            <div className="row">
                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 text-muted">Identifiant</h6>
                            <p className="card-text fs-5">{user.username}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 text-muted">Email</h6>
                            <p className="card-text">{user.email}</p>
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 text-muted">Membre depuis</h6>
                            <p className="card-text">
                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                }) : 'Date non disponible'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const BadgesTab = () => (
        <div className="card-body">
            <h5 className="card-title fw-bold mb-4">
                <FaTrophy className="me-2 text-warning" />
                {t('profileBadgesTitle') || 'Badges et Récompenses'}
            </h5>
            {user.badges && user.badges.length > 0 ? (
                <div className="row g-3">
                    {user.badges.map((badge, index) => (
                        <div key={index} className="col-md-6 col-lg-4">
                            <div className="card h-100 border-primary">
                                <div className="card-body text-center">
                                    <FaTrophy className="text-warning mb-3" size={48} />
                                    <h6 className="card-title">{badge}</h6>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="alert alert-info">
                    <p className="mb-0">
                        {t('profileNoBadges') || 'Aucun badge pour le moment. Continuez à utiliser la plateforme pour en débloquer !'}
                    </p>
                </div>
            )}
        </div>
    );

    const SecurityTab = () => {
        if (twoFactor.step === 'setup') {
            return <TwoFASetupComponent />;
        }

        return (
            <div className="card-body">
                <h5 className="card-title fw-bold mb-4">
                    <FaShieldAlt className="me-2 text-success" />
                    {t('profileSecurityTitle') || 'Sécurité du Compte'}
                </h5>

                {/* Section Mot de Passe */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="fw-bold mb-1">
                                    <FaLock className="me-2" />
                                    Mot de passe
                                </h6>
                                <p className="text-muted small mb-0">
                                    Mettez régulièrement à jour votre mot de passe
                                </p>
                            </div>
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => setShowPasswordForm(!showPasswordForm)}
                                disabled={twoFactor.isLoading}
                            >
                                {showPasswordForm ? 'Masquer' : 'Changer'}
                            </button>
                        </div>
                        {showPasswordForm && <ChangePasswordForm />}
                    </div>
                </div>

                {/* Section 2FA */}
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="fw-bold mb-1">
                                    <FaKey className="me-2" />
                                    Authentification à deux facteurs (2FA)
                                </h6>
                                <p className={`mb-0 ${user.otpVerified ? 'text-success' : 'text-danger'}`}>
                                    <strong>Statut :</strong> {user.otpVerified ? 'Activé' : 'Désactivé'}
                                </p>
                            </div>
                            <div>
                                {user.otpVerified ? (
                                    <button
                                        className="btn btn-outline-warning"
                                        onClick={twoFactor.disable}
                                        disabled={twoFactor.isLoading}
                                    >
                                        {twoFactor.isLoading ? (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        ) : null}
                                        Désactiver 2FA
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn-outline-success"
                                        onClick={twoFactor.startSetup}
                                        disabled={twoFactor.isLoading}
                                    >
                                        {twoFactor.isLoading ? (
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                        ) : null}
                                        Activer 2FA
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {twoFactor.message && twoFactor.step !== 'setup' && (
                            <div className="alert alert-info mt-3">
                                {twoFactor.message}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- Rendu Principal ---
    return (
        <div className="container py-5">
            <div className="row">
                {/* Colonne Gauche : Profil */}
                <div className="col-lg-4 mb-4">
                    <div className="card text-center shadow">
                        <div className="card-body">
                            <div className="position-relative mb-3">
                                <FaUserCircle className="text-primary" size={80} />
                                {user.otpVerified && (
                                    <div className="position-absolute top-0 end-0 bg-success rounded-circle p-1">
                                        <FaShieldAlt className="text-white" size={16} />
                                    </div>
                                )}
                            </div>
                            <h4 className="card-title fw-bold">{user.username}</h4>
                            <p className="text-muted">{user.email}</p>
                            
                            <div className="d-grid gap-2 mt-4">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setActiveTab('security')}
                                >
                                    <FaCog className="me-2" />
                                    {t('profileQuickSettings') || 'Paramètres de sécurité'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Colonne Droite : Onglets */}
                <div className="col-lg-8">
                    <div className="card shadow">
                        <div className="card-header bg-white border-bottom">
                            <ul className="nav nav-tabs nav-fill border-0">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'info' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('info')}
                                    >
                                        <FaUserCircle className="me-2" />
                                        {t('profileInfo') || 'Informations'}
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'badges' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('badges')}
                                    >
                                        <FaTrophy className="me-2" />
                                        {t('profileBadges') || 'Badges'}
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('security')}
                                    >
                                        <FaShieldAlt className="me-2" />
                                        {t('profileSecurity') || 'Sécurité'}
                                    </button>
                                </li>
                            </ul>
                        </div>
                        
                        <div className="card-body">
                            {activeTab === 'info' && <InfoTab />}
                            {activeTab === 'badges' && <BadgesTab />}
                            {activeTab === 'security' && <SecurityTab />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;